import { Route } from '@angular/router';
import { Observable } from 'rxjs';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { AuthOnlyGuard } from 'app/core/auth/guards/auth-only.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { BrandingDataResolver, InitialDataResolver, NavigationResolver } from 'app/app.resolvers';
import { ConfigurationsResolver } from 'app/shared/shared.resolvers';
import { InstanceSelector } from './instance-selector/pages/instance-selector';
// import { Home } from "app/home/pages/home/home";
import { Home } from 'app/home/pages/home/home';

// TODO: Change name of the routes spanish to english
// TODO: Change the iccons of the lenguages modale. like the app brandbot

// @formatter:off

export const appRoutes: Route[] = [
  // Redirect empty path to '/dashboards/project'
  { path: '', pathMatch: 'full', redirectTo: 'home' },

  // Redirect signed-in user to the '/dashboards/project'
  //
  // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
  // path. Below is another redirection for that path to redirect the user to the desired
  // location. This is a small convenience to keep all main routes together here on this file.

  {
    path: 'signed-in-redirect',
    pathMatch: 'full',
    redirectTo: 'home',
  },

  // Auth routes for guests
  {
    path: 'auth',
    canMatch: [NoAuthGuard],
    component: LayoutComponent,
    data: {
      layout: 'empty',
    },
    resolve: {
      initialData: BrandingDataResolver,
    },
    children: [
      {
        path: 'confirmation-required',
        loadChildren: () =>
          import('app/auth/confirmation-required/confirmation-required-routing').then(
            (m) => m.authConfirmationRequiredRoutes
          ),
      },
      {
        path: 'forgot-password',
        data: { titleKey: 'FORGOT_PASSWORD' },
        loadChildren: () =>
          import('app/auth/forgot-password/forgot-password-routing').then(
            (m) => m.authForgotPasswordRoutes
          ),
      },
      {
        path: 'reset-password',
        loadChildren: () =>
          import('app/auth/reset-password/reset-password-routing').then(
            (m) => m.authResetPasswordRoutes
          ),
      },
      {
        path: 'sign-in',
        data: { titleKey: 'SIGN_IN' },
        loadChildren: () =>
          import('app/auth/sign-in/sign-in-routing').then((m) => m.authSignInRoutes),
      },
      {
        path: 'verify-2fa',
        data: { titleKey: 'TWO_FACTOR_TITLE' },
        loadComponent: () =>
          import('app/auth/verify-2fa/pages/verify-2fa').then((m) => m.AuthVerify2fa),
      },
      {
        path: 'sign-in-admin',
        data: {
          titleKey: 'SIGN_IN_ADMIN',
          admin: true,
        },
        loadChildren: () =>
          import('app/auth/sign-in-account/sign-in-account-routing').then(
            (m) => m.authSignInAccountRoutes
          ),
      },
      {
        path: 'sign-in-employee',
        data: {
          titleKey: 'SIGN_IN',
          admin: false,
        },
        loadChildren: () =>
          import('app/auth/sign-in-account/sign-in-account-routing').then(
            (m) => m.authSignInAccountRoutes
          ),
      },
      {
        path: 'sign-up-request',
        data: { titleKey: 'SIGN_UP_REQUEST' },
        loadChildren: () =>
          import('app/auth/sign-up-request/sign-up-request-routing').then(
            (m) => m.authSignUpRequestRoutes
          ),
      },
    ],
  },
  {
    path: 'select-instance',
    data: {
      layout: 'empty-selector',
      titleKey: 'INSTANCES',
    },
    // Signed in, no instance required: AuthGuard would bounce back here when
    // none is chosen yet, so it is AuthOnlyGuard. Without a guard a signed-out
    // visit rendered a broken page instead of the sign-in.
    canMatch: [AuthOnlyGuard],
    component: LayoutComponent,
    // Only branding blocks here (the empty-selector layout needs it). The
    // instance list is loaded non-blocking inside InstanceSelector.ngOnInit
    // (brandbot pattern) so the shell + skeleton cards render immediately
    // instead of waiting on the ~1.2s Instances/user call.
    resolve: {
      initialData: BrandingDataResolver,
    },
    children: [
      {
        path: '',
        component: InstanceSelector,
      },
    ],
  },
  {
    path: 'auth',
    data: {
      layout: 'empty',
    },
    component: LayoutComponent,
    resolve: {
      initialData: BrandingDataResolver,
    },
    children: [
      {
        path: 'sign-up',
        loadChildren: () =>
          import('app/auth/sign-up/sign-up-routing').then((m) => m.authSignUpRoutes),
      },
      {
        path: 'accept-invitation',
        canMatch: [AuthOnlyGuard],
        loadChildren: () =>
          import('app/auth/accept-invitation/accept-invitation-routing').then(
            (m) => m.acceptInvitationRoutes
          ),
      },
    ],
  },
  // A report printed by the report service for an automatic email (the Monday
  // management summary). No session and no resolvers: the page draws only the report
  // it is handed in `window.__PC_PRINT__` and asks the API for nothing.
  {
    path: 'print/opportunity-report',
    data: { print: true, titleKey: 'REPORT_TITLE' },
    loadComponent: () =>
      import('app/customer-support/opportunities/report/opportunity-report-page').then(
        (m) => m.OpportunityReportPage
      ),
  },
  // Print reports: no sidebar or header, so the page prints as the report itself.
  // Opened in their own tab from a list's export dialog.
  {
    path: 'reports',
    canMatch: [AuthGuard],
    component: LayoutComponent,
    data: {
      layout: 'empty',
    },
    resolve: {
      initialData: InitialDataResolver,
      configuration: ConfigurationsResolver,
    },
    children: [
      {
        path: 'opportunities',
        data: { titleKey: 'REPORT_TITLE' },
        loadComponent: () =>
          import('app/customer-support/opportunities/report/opportunity-report-page').then(
            (m) => m.OpportunityReportPage
          ),
      },
      // Quotes and sales orders share one report page; `kind` picks which.
      {
        path: 'quotes',
        data: { titleKey: 'QUOTE_REPORT_TITLE', kind: 'quotes' },
        loadComponent: () =>
          import('app/sales/reports/sales-report-page').then((m) => m.SalesReportPage),
      },
      {
        path: 'sales-orders',
        data: { titleKey: 'SALES_ORDER_REPORT_TITLE', kind: 'sales-orders' },
        loadComponent: () =>
          import('app/sales/reports/sales-report-page').then((m) => m.SalesReportPage),
      },
    ],
  },
  {
    path: '',
    canMatch: [AuthGuard],
    component: LayoutComponent,
    resolve: {
      initialData: InitialDataResolver,
      navigation: NavigationResolver,
    },
    children: [
      {
        path: 'home',
        data: { titleKey: 'HOME' },
        // Analytics load from Home itself (the metric cards fill
        // in when the call lands) — as a blocking resolver it held the
        // splash screen for one extra round trip on every entry.
        component: Home,
      },
      {
        // Mockup — knowledge base UI with demo data (no backend module yet).
        path: 'company-knowledge',
        data: { titleKey: 'COMPANY_KNOWLEDGE' },
        loadComponent: () =>
          import('app/company-knowledge/pages/list/list').then((m) => m.CompanyKnowledgeListPage),
      },
      {
        // Mockup — tasks module with demo data (no backend module yet).
        path: 'tasks',
        data: { titleKey: 'TASKS' },
        loadComponent: () => import('app/tasks/pages/list/list').then((m) => m.TasksListPage),
      },
      {
        // Mockup — Tempo-style project time tracking with demo data.
        path: 'project-hours',
        data: { titleKey: 'PROJECT_HOURS' },
        loadComponent: () =>
          import('app/project-hours/pages/list/list').then((m) => m.ProjectHoursListPage),
      },
      {
        // Mockup — developer edition: GitHub sync + PrimeControl MCP panel.
        path: 'github',
        data: { titleKey: 'GITHUB' },
        loadComponent: () => import('app/github/pages/list/list').then((m) => m.GithubListPage),
      },
      {
        path: 'profile',
        children: [
          {
            path: 'preferences',
            data: { titleKey: 'preferences' },
            // Typed structurally so the lazy page is not pulled into the main bundle.
            canDeactivate: [(page: { confirmLeave(): Observable<boolean> }) => page.confirmLeave()],
            loadComponent: () =>
              import('app/profile/preferences/pages/profile-preferences/profile-preferences').then(
                (m) => m.ProfilePreferences
              ),
          },
          {
            path: 'security',
            data: { titleKey: 'SECURITY' },
            loadComponent: () =>
              import('app/profile/security/pages/security').then((m) => m.ProfileSecurity),
          },
          {
            path: 'organizations',
            data: { titleKey: 'ORGANIZATIONS' },
            loadComponent: () =>
              import('app/profile/organizations/pages/list/list').then(
                (m) => m.ProfileOrganizationsList
              ),
          },
          {
            path: 'organizations/:organizationId',
            data: { titleKey: 'ORGANIZATIONS' },
            loadComponent: () =>
              import('app/profile/organizations/pages/details/details').then(
                (m) => m.ProfileOrganizationDetail
              ),
          },
        ],
      },
      {
        path: 'customer-support',
        children: [
          {
            path: 'contacts',
            data: { titleKey: 'CONTACTS' },
            loadChildren: () =>
              import('app/customer-support/contacts/contacts-routing').then(
                (m) => m.contactsRoutes
              ),
          },
          {
            path: 'customers',
            data: { titleKey: 'CUSTOMERS' },
            loadChildren: () =>
              import('app/customer-support/clients/clients-routing').then((m) => m.customersRoutes),
          },
          {
            path: 'agents',
            data: { titleKey: 'AGENTS' },
            loadChildren: () =>
              import('app/customer-support/agents/agents-routing').then((m) => m.agentsRouter),
          },
          {
            path: 'groups',
            data: { titleKey: 'CUSTOMER_GROUPS' },
            loadChildren: () =>
              import('app/customer-support/groups/groups-routing').then((m) => m.groupsRoutes),
          },
          {
            path: 'opportunities',
            data: { titleKey: 'OPPORTUNITIES' },
            loadChildren: () =>
              import('app/customer-support/opportunities/opportunities-routing').then(
                (m) => m.opportunitiesRoutes
              ),
          },
          {
            path: 'campaigns',
            data: { titleKey: 'CAMPAIGNS' },
            loadChildren: () =>
              import('app/customer-support/campaigns/campaigns-routing').then((m) => m.default),
          },
          {
            path: 'distribution-lists',
            data: { titleKey: 'DISTRIBUTION_LIST' },
            loadChildren: () =>
              import('app/customer-support/distribution-directory/distribution-directory-routing').then(
                (m) => m.distributionAssetListsRoutes
              ),
          },

          {
            path: 'templates',
            data: { titleKey: 'TEMPLATES' },
            loadChildren: () =>
              import('app/customer-support/templates/templates-routing').then(
                (m) => m.templatesRoutes
              ),
          },
          {
            path: 'blacklist',
            data: { titleKey: 'BLACKLIST_TITLE' },
            loadChildren: () =>
              import('app/customer-support/blacklist/blacklist-routing').then(
                (m) => m.blacklistRoutes
              ),
          },
          {
            path: 'campaign-history',
            data: {
              titleKey: 'CAMPAIGN_HISTORY',
            },
            loadChildren: () =>
              import('app/customer-support/campaign-history/campaign-history-routing').then(
                (m) => m.routes_messages
              ),
          },
          {
            path: 'chat',
            data: { titleKey: 'CHAT' },
            loadChildren: () =>
              import('app/customer-support/chat/chat-routing').then((m) => m.default),
          },
        ],
      },
      {
        path: 'sales',
        children: [
          {
            path: 'quotes',
            data: { titleKey: 'QUOTES' },
            loadChildren: () =>
              import('app/sales/quotes/quotes-routing').then((m) => m.quotesRoutes),
          },
          {
            path: 'products',
            data: { titleKey: 'PRODUCTS' },
            loadChildren: () =>
              import('app/sales/products/products-routing').then((m) => m.productsRoutes),
          },
          {
            path: 'conditions',
            data: { titleKey: 'CONDITIONS' },
            loadChildren: () =>
              import('app/sales/conditions/conditions-routing').then((m) => m.condtionsRoutes),
          },
          {
            path: 'product-groups',
            data: { titleKey: 'PRODUCT_GROUPS' },
            loadChildren: () =>
              import('app/sales/product-groups/product-groups-routing').then(
                (m) => m.ProductGroupsRoutes
              ),
          },
          {
            path: 'sales-orders',
            data: { titleKey: 'SALES_ORDERS' },
            loadChildren: () =>
              import('app/sales/sales-orders/sales-orders-routing').then(
                (m) => m.salesOrdersRouter
              ),
          },
          {
            path: 'projects-profitability',
            data: { titleKey: 'PROJECTS-PROFITABILITY' },
            loadChildren: () =>
              import('app/sales/projects-profitability/projects-profitability-routing').then(
                (m) => m.ProjectProfitabilitysRouter
              ),
          },
          {
            path: 'quotation-history',
            data: {
              titleKey: 'QUOTATION_HISTORY',
            },
            loadChildren: () =>
              import('app/sales/quotation-history/quotation-history-routing').then(
                (m) => m.routes_messages
              ),
          },
          {
            path: 'sales-orders-history',
            data: {
              titleKey: 'SALES_ORDERS_HISTORY',
            },
            loadChildren: () =>
              import('app/sales/sales-orders-history/sales-orders-history-routing').then(
                (m) => m.routes_messages
              ),
          },
        ],
      },
      {
        path: 'purchases',
        children: [
          {
            path: 'suppliers',
            data: { titleKey: 'SUPPLIERS' },
            loadChildren: () =>
              import('app/purchases/suppliers/suppliers-routing').then((m) => m.suppliersRoutes),
          },
          {
            path: 'sales-offers',
            data: { titleKey: 'SALES_OFFERS' },
            loadChildren: () =>
              import('app/purchases/sales-offers/sales-offers-routing').then(
                (m) => m.salesOffersRouter
              ),
          },
          {
            path: 'purchase-offers',
            data: { titleKey: 'PURCHASE_OFFERS' },
            loadChildren: () =>
              import('app/purchases/purchase-offers/purchase-offers-routing').then(
                (m) => m.purchaseOffersRouter
              ),
          },
          {
            path: 'transactions',
            data: { titleKey: 'TRANSACTIONS' },
            loadChildren: () =>
              import('app/purchases/sale-offer-transactions/sale-offer-transactions-routing').then(
                (m) => m.saleOfferTransactiosRouter
              ),
          },
        ],
      },
      {
        path: 'human-talent',
        children: [
          // Departments merged into the combined collaborators page —
          // keep the old URLs working.
          {
            path: 'departments/:id',
            redirectTo: '/human-talent/collaborators/department/:id',
          },
          {
            path: 'departments',
            pathMatch: 'full',
            redirectTo: '/human-talent/collaborators?tab=departments',
          },
          {
            path: 'employee-positions',
            data: { titleKey: 'EMPLOYEE_POSITIONS' },
            loadChildren: () =>
              import('app/human-talent/employee-positions/employee-positions-routing').then(
                (m) => m.employeePositionsRouter
              ),
          },
          {
            path: 'skills',
            data: { titleKey: 'SKILLS' },
            loadChildren: () =>
              import('app/human-talent/skills/skills-routing').then((m) => m.skillsRouter),
          },
          {
            path: 'collaborators',
            data: { titleKey: 'COLLABORATORS' },
            loadChildren: () =>
              import('app/human-talent/collaborators/collaborators-routing').then(
                (m) => m.collaboratorsRouter
              ),
          },
        ],
      },
      {
        path: 'logistics',
        children: [
          {
            path: 'projects',
            data: { titleKey: 'PROJECTS' },
            loadChildren: () =>
              import('app/logistics/projects/projects-routing').then((m) => m.projectsRoutes),
          },
          {
            path: 'inspections',
            data: { titleKey: 'INSPECTIONS' },
            loadChildren: () =>
              import('app/logistics/inspections/inspections-routing').then(
                (m) => m.inspectionsRoutes
              ),
          },
          {
            path: 'machineries',
            data: { titleKey: 'MACHINERIES' },
            loadChildren: () =>
              import('app/logistics/machineries/machineries-routing').then(
                (m) => m.machineriesRouter
              ),
          },
          {
            path: 'fleets',
            data: { titleKey: 'FLEETS' },
            loadChildren: () =>
              import('app/logistics/fleets/fleets-routing').then((m) => m.fleetsRouter),
          },
          {
            path: 'plans',
            data: { titleKey: 'PLANS' },
            loadChildren: () =>
              import('app/logistics/plans/plans-routing').then((m) => m.plansRouter),
          },
        ],
      },
      {
        path: 'settings',
        children: [
          {
            path: 'instances',
            data: { titleKey: 'INSTANCES' },
            loadChildren: () =>
              import('app/settings/instances/instances-routing').then((m) => m.instancesRoutes),
          },
          {
            path: 'modules',
            data: { titleKey: 'MODULES' },
            loadChildren: () =>
              import('app/settings/modules/modules-routing').then((m) => m.modulesRoutes),
          },
          // Roles live in the workspace settings now (list + a page per role);
          // the old instance-less list is gone, and its link lands there.
          {
            path: 'roles',
            pathMatch: 'full',
            redirectTo: () => {
              const id =
                sessionStorage.getItem('primecontrol_idInstance') ??
                localStorage.getItem('primecontrol_idInstance');
              return id ? `/settings/instances/${id}/roles` : '/select-instance';
            },
          },
        ],
      },
    ],
  },
];
